import { DaysOfTheWeekNames, } from 'design-system/day-of-the-week-picker/values';
import { Frequencies, FrequencyTypes, } from 'reducers/types';
import i18n from 'utils/i18n';
export const MAX_BUDGET_AMOUNT = 99_999_999;
const nth = (d) => {
    if (d > 3 && d < 21)
        return `${d}th`;
    switch (d % 10) {
        case 1:
            return `${d}st`;
        case 2:
            return `${d}nd`;
        case 3:
            return `${d}rd`;
        default:
            return `${d}th`;
    }
};
export const incomeToPeriod = (income) => {
    if (!income) {
        return {
            frequency: Frequencies.Monthly,
        };
    }
    const { schedule: { recurrence }, currentPaydayRange, } = income;
    if ('debugRRule' in recurrence) {
        return {
            frequency: Frequencies.Minutely,
        };
    }
    let frequency;
    const { frequency: frequencyType, byMonthDay, interval, onHoliday, weekday, weekdays, bySetPos, weekNumber, } = recurrence;
    if (frequencyType === FrequencyTypes.Monthly) {
        switch (true) {
            case byMonthDay?.length === 2:
                frequency = Frequencies.TwiceAMonth;
                break;
            case byMonthDay?.[0] === 31 && onHoliday === 'BEFORE':
                frequency = Frequencies.LastWorkingDay;
                break;
            case weekdays?.length === 5 && bySetPos === -2:
                frequency = Frequencies.PenultimateWorkingDay;
                break;
            case weekdays?.length === 5 && !!bySetPos:
                frequency = Frequencies.NthLastWorkingDay;
                break;
            case !weekday && !weekNumber:
                frequency = Frequencies.FixedDate;
                break;
            case Boolean(weekday) && Boolean(weekNumber):
                frequency = Frequencies.EveryMonth;
                break;
            case weekday === 5 && byMonthDay?.length === 7:
                frequency = Frequencies.EveryMonth;
                break;
            default:
        }
    }
    else {
        switch (interval) {
            case 4:
                frequency = Frequencies.EveryFourWeeks;
                break;
            case 2:
                frequency = Frequencies.EveryTwoWeeks;
                break;
            default:
                frequency = Frequencies.Weekly;
        }
    }
    return {
        onHoliday,
        days: byMonthDay,
        startDate: currentPaydayRange?.from,
        frequency,
        weekday,
        bySetPos,
        weekNumber,
    };
};
export const toBudgetApiPayload = ({ days, weekday, weekNumber, frequency, onHoliday, startDate, bySetPos, } = {}) => {
    const lastWorkingDay = {
        schedule: {
            recurrence: {
                byMonthDay: [31],
                frequency: FrequencyTypes.Monthly,
                interval: 1,
                onHoliday: 'BEFORE',
            },
        },
    };
    switch (frequency) {
        case Frequencies.FixedDate:
            return {
                schedule: {
                    recurrence: {
                        byMonthDay: days,
                        frequency: FrequencyTypes.Monthly,
                        interval: 1,
                        onHoliday,
                    },
                },
            };
        case Frequencies.LastWorkingDay:
            return lastWorkingDay;
        case Frequencies.EveryMonth:
            return {
                schedule: {
                    recurrence: {
                        frequency: FrequencyTypes.Monthly,
                        interval: 1,
                        weekday,
                        weekNumber,
                        onHoliday,
                    },
                },
            };
        case Frequencies.TwiceAMonth:
            return {
                schedule: {
                    recurrence: {
                        byMonthDay: days,
                        frequency: FrequencyTypes.Monthly,
                        interval: 1,
                        onHoliday,
                    },
                },
            };
        case Frequencies.EveryTwoWeeks:
            return {
                schedule: {
                    start: startDate,
                    recurrence: {
                        frequency: FrequencyTypes.Weekly,
                        interval: 2,
                        onHoliday,
                        weekday,
                    },
                },
            };
        case Frequencies.EveryFourWeeks:
            return {
                schedule: {
                    start: startDate,
                    recurrence: {
                        frequency: FrequencyTypes.Weekly,
                        interval: 4,
                        onHoliday,
                        weekday,
                    },
                },
            };
        case Frequencies.Weekly:
            return {
                schedule: {
                    recurrence: {
                        frequency: FrequencyTypes.Weekly,
                        interval: 1,
                        onHoliday,
                        weekday,
                    },
                },
            };
        case Frequencies.Monthly:
            return {
                schedule: {
                    recurrence: {
                        byMonthDay: days,
                        frequency: FrequencyTypes.Monthly,
                        interval: 1,
                        onHoliday,
                    },
                },
            };
        case Frequencies.Minutely:
            return {
                schedule: {
                    recurrence: {
                        debugRRule: {
                            freq: 5, // sets the frequency type to Minutes, don’t change this
                            interval: 15, // sets the interval in minutes eg. 15 = 15mins
                        },
                    },
                },
            };
        case Frequencies.PenultimateWorkingDay:
            return {
                schedule: {
                    recurrence: {
                        frequency: FrequencyTypes.Monthly,
                        interval: 1,
                        weekdays: [1, 2, 3, 4, 5],
                        bySetPos: -2,
                        onHoliday: 'BEFORE',
                    },
                },
            };
        case Frequencies.NthLastWorkingDay: {
            if (bySetPos === -1)
                return lastWorkingDay;
            return {
                schedule: {
                    recurrence: {
                        frequency: FrequencyTypes.Monthly,
                        interval: 1,
                        weekdays: [1, 2, 3, 4, 5],
                        bySetPos,
                        onHoliday: 'BEFORE',
                    },
                },
            };
        }
        default:
            return null;
    }
};
export const toFrequencyText = ({ weekday, frequency, weekNumber, days, bySetPos } = {}, defaultText) => {
    switch (frequency) {
        case Frequencies.EveryTwoWeeks:
            return `${i18n.t('Every2weeks')} on ${DaysOfTheWeekNames[weekday]}`;
        case Frequencies.EveryFourWeeks:
            return `Every 4 weeks on ${DaysOfTheWeekNames[weekday]}`;
        case Frequencies.EveryMonth: {
            if (weekNumber) {
                return `On the ${weekNumber === 5 ? 'last' : nth(weekNumber)} ${DaysOfTheWeekNames[weekday]} of every month`;
            }
            return `On the last ${DaysOfTheWeekNames[weekday]} of every month`;
        }
        case Frequencies.Monthly:
            return `First day of the month`;
        case Frequencies.Minutely:
            return `Every 15 mins`;
        case Frequencies.TwiceAMonth:
            return `${nth(days?.[0])} & ${nth(days?.[1])} of every month`;
        case Frequencies.LastWorkingDay:
            return 'On the last working day';
        case Frequencies.FixedDate:
            return `${nth(days?.[0])} of every month`;
        case Frequencies.Weekly:
            return `Every ${DaysOfTheWeekNames[weekday]}`;
        case Frequencies.PenultimateWorkingDay:
            return `Every penultimate working day`;
        case Frequencies.NthLastWorkingDay:
            return `Every ${nth(Math.abs(bySetPos))} last working day`;
        default:
            return defaultText;
    }
};
export const toPeriodCadenceText = ({ frequency } = {}) => {
    switch (frequency) {
        case Frequencies.Weekly:
            return 'each week';
        case Frequencies.EveryTwoWeeks:
            return 'every two weeks';
        case Frequencies.EveryFourWeeks:
            return 'every four weeks';
        case Frequencies.TwiceAMonth:
            return 'each period';
        default:
            return 'each month';
    }
};
export const getFrequencyType = (fr) => {
    if ([
        Frequencies.Weekly,
        Frequencies.EveryTwoWeeks,
        Frequencies.EveryFourWeeks,
        Frequencies.FixedDate,
    ].includes(fr)) {
        return FrequencyTypes.Weekly;
    }
    return FrequencyTypes.Monthly;
};
var PeriodFrequencyEnum;
(function (PeriodFrequencyEnum) {
    PeriodFrequencyEnum["Monthly"] = "Monthly";
    PeriodFrequencyEnum["BiWeekly"] = "BiWeekly";
    PeriodFrequencyEnum["Weekly"] = "Weekly";
})(PeriodFrequencyEnum || (PeriodFrequencyEnum = {}));
export const getPeriodFrequency = (fr) => {
    if ([
        Frequencies.Monthly,
        Frequencies.PenultimateWorkingDay,
        Frequencies.LastWorkingDay,
        Frequencies.EveryMonth,
        Frequencies.FixedDate,
        Frequencies.Minutely,
        Frequencies.EveryFourWeeks,
        Frequencies.NthLastWorkingDay,
    ].includes(fr)) {
        return PeriodFrequencyEnum.Monthly;
    }
    if ([Frequencies.TwiceAMonth, Frequencies.EveryTwoWeeks].includes(fr)) {
        return PeriodFrequencyEnum.BiWeekly;
    }
    return PeriodFrequencyEnum.Weekly;
};
export const getFontSize = (amount, ignoreNegativeSign = false) => {
    const absDigits = Math.floor(Math.abs(amount)).toString().length;
    // eslint-disable-next-line no-nested-ternary
    const charLength = ignoreNegativeSign
        ? absDigits
        : amount < 0
            ? absDigits + 1
            : absDigits;
    if (charLength > 7) {
        return { 'Numbers-16': true };
    }
    if (charLength > 6) {
        return { 'Numbers-18': true };
    }
    if (charLength > 4) {
        return { 'Numbers-20': true };
    }
    return { 'Numbers-24': true };
};
export const getMonthToPeriodRatio = (body) => {
    let monthToPeriodRatio = 1;
    const schedule = body?.schedule;
    if (schedule) {
        // weekly income period
        if (schedule.recurrence.frequency === 'WEEKLY')
            if (schedule.recurrence.interval === 2) {
                monthToPeriodRatio = 2;
            }
            else if (schedule.recurrence.interval === 4) {
                monthToPeriodRatio = 1;
            }
            else {
                monthToPeriodRatio = 4;
            }
        // bi-weekly income period
        else if (schedule.recurrence.byMonthDay?.length === 2)
            monthToPeriodRatio = 2;
    }
    return monthToPeriodRatio;
};
export const findMerchantInMostRecentPeriod = (monthlyMerchants, monthlyTotalsWithBudgets, merchantId) => {
    if (monthlyTotalsWithBudgets.length === 0) {
        return undefined;
    }
    const mostRecentKey = `${monthlyTotalsWithBudgets[0].from}|${monthlyTotalsWithBudgets[0].to}`;
    const merchantsInPeriod = monthlyMerchants[mostRecentKey];
    if (!merchantsInPeriod?.merchants) {
        return undefined;
    }
    return merchantsInPeriod.merchants.find((m) => m.id === merchantId);
};
